import type { AllowedActionable } from 'pkg/api/models/allowed_actionable';
import type { Dateable } from 'pkg/api/models/dateable';
import type { Record } from 'pkg/api/models/record';
import type { TaxRate } from 'pkg/api/models/tax_rate';

export enum Provider {
	Xero = 'xero',
	QuickBooks = 'quickbooks',
	LOK = 'lok',
}

export enum Type {
	Accounting = 'accounting',
	Reporting = 'reporting',
}

export enum Status {
	Disconnected = 'disconnected',
	Connected = 'connected',
	Error = 'error',
}

export enum ErrorCode {
	Unauthorized = 'unauthorized',
	Forbidden = 'forbidden',
	RefreshFailed = 'refresh_failed',
	OrgNotFound = 'org_not_found',
}

// Unsaved (id 0) while the integration was never connected.
export interface IntegrationConnection
	extends Record, Dateable, AllowedActionable {
	groupId: number;
	provider: Provider;
	type: Type;
	status: Status;
	connectedByUserId: number;
	connectedAt: number;
	errorCode: ErrorCode;
	externalAccountId: string;
	externalAccountName: string;
	// Syncs are paused until then after the provider rate limited them.
	rateLimitedUntil: number;
}

export interface Integration {
	provider: Provider;
	type: Type;
	name: string;
	description: string;
	// Path of the logo in the app's assets.
	logo: string;
	connection: IntegrationConnection;
	// Why the current user may not connect or change it; empty when they may.
	unavailableText: string;
	// Whether a connected integration is set up to sync.
	isComplete: boolean;
	lokSettings?: LOKSettings;
}

export interface LOKSettings {
	fromTime: number;
}

export interface ConnectLOKPayload {
	organizationNumber: string;
	fromTime: number;
}

export interface IntegrationCategory {
	type: Type;
	label: string;
	integrations: Integration[];
}

export interface ConnectSession {
	token: string;
	expiresAt: string;
}

export interface XeroTenant {
	tenantId: string;
	tenantType: string;
	tenantName: string;
}

export interface XeroAccount {
	id: string;
	code: string;
	name: string;
}

export interface XeroAccounts {
	salesAccounts: XeroAccount[];
	// The payment and payout accounts are picked from these.
	bankAccounts: XeroAccount[];
	feeAccounts: XeroAccount[];
}

export interface XeroContact {
	id: string;
	name: string;
}

export interface XeroTaxRate {
	taxType: string;
	name: string;
	effectiveRate: number;
}

// Keyed by our tax rate id, valued with a Xero TaxType.
export type XeroTaxMapping = { [taxRateId: string]: string };

export enum XeroBasis {
	Unknown = 'unknown',
	Accrual = 'accrual',
	Cash = 'cash',
}

// Unsaved (id 0) while the picked Xero organisation has no settings.
export interface XeroSettings extends Record, Dateable {
	integrationConnectionId: number;
	tenantId: string;
	basis: XeroBasis;
	salesAccountCode: string;
	paymentAccountId: string;
	payoutAccountId: string;
	feeAccountCode: string;
	feeContactId: string;
	feeContactName: string;
	taxMapping: XeroTaxMapping;
	noTaxType: string;
	// When the settings were first saved complete; nothing before it is synced.
	syncFrom: number;
}

export interface XeroSettingsDetails {
	settings: XeroSettings;
	taxRates: TaxRate[];
	isComplete: boolean;
}

export interface XeroSettingsPayload {
	basis: XeroBasis;
	salesAccountCode: string;
	paymentAccountId: string;
	payoutAccountId: string;
	feeAccountCode: string;
	feeContactId: string;
	taxMapping: XeroTaxMapping;
	noTaxType: string;
}
